import { useQuery } from "react-query";
import { propelyApi } from "../../libs/firebase/api";
import { IRawDeviation, ITask, TApiGetTasksResponse, TRawDeviation, TTask } from "@propely/propely-typings";
import { convertApiTaskToRawTask } from "@src/to-be-removed/api/deviations/deviations.types";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { queryClient } from "@src/index";

type GetFirebaseTaskByIDInput = {
  propertyId: string | undefined;
  deviationId: string | undefined;
};

const useFirebaseTaskKey = (input: GetFirebaseTaskByIDInput) => ["firebase-tasks", { propertyId: input.propertyId, taskID: input.deviationId }];

const fetchTask = async (input: GetFirebaseTaskByIDInput) => {
  if (!input.propertyId || !input.deviationId) return undefined;

  const task = await getDoc(doc(getFirestore(), "properties", input.propertyId, "deviations", input.deviationId));
  return TTask.parse({
    id: task.id,
    ...task.data(),
  });
};

const fetchFirebaseTasks = async (inputs: GetFirebaseTaskByIDInput[]): Promise<ITask[]> => {
  const promises = inputs.map((input) => fetchTask(input));
  const results = await Promise.allSettled(promises);

  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      const data = result.value;
      const input = inputs[index];

      queryClient.setQueryData(useFirebaseTaskKey(input), data);
    }
  });

  return results.filter((result): result is PromiseFulfilledResult<ITask> => result.status === "fulfilled").map((result) => result.value);
};

export const useFirebaseTaskByID = (input: GetFirebaseTaskByIDInput) => {
  const $deviation = useQuery(useFirebaseTaskKey(input), () => fetchTask(input));
  return {
    $deviation,
    deviation: $deviation.data,
  };
};

export const useFirebaseTasksByIDs = (ids: { propertyID: string; taskID: string }[]) => {
  const $tasks = useQuery(["firebase-tasks", ids], () =>
    fetchFirebaseTasks(ids.map((id) => ({ propertyId: id.propertyID, deviationId: id.taskID })))
  );
  return {
    $tasks,
    tasks: $tasks.data ?? [],
    isLoading: $tasks.isLoading,
  };
};

export interface GetTasksAPIInput {
  propertyID?: string;
  archived?: boolean;
  status?: number[];
  priority?: number[];
  executors?: string[];
  responsibles?: string[];
  taskTypeID?: string[];
  tags?: string[];
}

const getParams = (input: GetTasksAPIInput) => {
  const params = new URLSearchParams();
  if (input.propertyID !== undefined) params.append("propertyID", input.propertyID);
  if (input.archived !== undefined) params.append("archived", input.archived.toString());
  if (input.status !== undefined) params.append("status", input.status.toString());
  if (input.priority !== undefined) params.append("priority", input.priority.toString());
  if (input.executors !== undefined) params.append("executors", input.executors.toString());
  if (input.responsibles !== undefined) params.append("responsibles", input.responsibles.toString());
  if (input.taskTypeID !== undefined) params.append("taskTypeID", input.taskTypeID.toString());
  if (input.tags !== undefined) params.append("tags", input.tags.toString());

  return params;
};

export const loadAllApiTasks = async (input: GetTasksAPIInput): Promise<IRawDeviation[]> => {
  const params = getParams(input);

  const response = await propelyApi().get(`/tasks?${params.toString()}`);
  const parsedDeviations = TApiGetTasksResponse.parse(response.data).datas;
  const convertedDeviations = parsedDeviations.map((deviation) => convertApiTaskToRawTask(deviation));

  return convertedDeviations;
};
